/**
 * Fetches the currently authenticated user (including `role`, `is_active` and
 * `is_superuser`) and derives convenience capability flags used to gate the
 * settings management surfaces.
 *
 * The profile is fetched once and memoised on the module so repeated mounts
 * (e.g. switching between settings tabs) don't refetch it on every render.
 */
import { useEffect, useState } from 'react';
import api from '../services/api';

let cachedUser = null;
let inFlight = null;

const fetchProfile = () => {
  if (cachedUser) return Promise.resolve(cachedUser);
  if (!inFlight) {
    inFlight = api
      .get('auth/user/')
      .then((res) => {
        cachedUser = res.data;
        return cachedUser;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

/** Clear the cached profile (call after the role/identity may have changed). */
export const clearCurrentUserCache = () => {
  cachedUser = null;
};

/**
 * @returns {{ user: object|null, loading: boolean, error: any,
 *   isSuperAdmin: boolean, isAdmin: boolean, canManageUsers: boolean,
 *   canCreateWorkspace: boolean }}
 */
const useCurrentUser = () => {
  const [user, setUser] = useState(cachedUser);
  const [loading, setLoading] = useState(!cachedUser);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (cachedUser) {
      setUser(cachedUser);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchProfile()
      .then((data) => {
        if (active) {
          setUser(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (active) setError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const isSuperAdmin = Boolean(user?.is_superuser);
  const isAdmin = isSuperAdmin || user?.role === 'admin';

  return {
    user,
    loading,
    error,
    isSuperAdmin,
    isAdmin,
    isGuest: user?.role === 'guest',
    guestPermissions: user?.guest_permissions || null,
    canManageUsers: isAdmin,
    canCreateWorkspace: isAdmin,
  };
};

/**
 * Resolve a guest's capability flags for a given workspace from the profile's
 * `guest_permissions` map. Non-guests (and unconfigured workspaces) are treated
 * as fully unrestricted so existing gating for admins/users is unaffected.
 *
 * @param {object|null} user  the profile object from `useCurrentUser`
 * @param {number|string|null} workspaceId
 * @returns {{ isGuest: boolean, canManageTasks: boolean,
 *   canManageActionItems: boolean, canManageChecklists: boolean,
 *   canEditItems: boolean, canComment: boolean,
 *   allowedProjectIds: number[], allowedEpicIds: number[] }}
 */
export const guestCapabilities = (user, workspaceId) => {
  const isGuest = user?.role === 'guest';
  if (!isGuest) {
    return {
      isGuest: false,
      canManageTasks: true,
      canManageActionItems: true,
      canManageChecklists: true,
      canEditItems: true,
      canComment: true,
      allowedProjectIds: null, // null => no restriction
      allowedEpicIds: null,
    };
  }
  const perms = user?.guest_permissions || {};
  const caps = (workspaceId != null && perms[String(workspaceId)]) || {};
  return {
    isGuest: true,
    canManageTasks: Boolean(caps.can_manage_tasks),
    canManageActionItems: Boolean(caps.can_manage_action_items),
    canManageChecklists: Boolean(caps.can_manage_checklists),
    canEditItems: Boolean(caps.can_edit_items),
    canComment: Boolean(caps.can_comment),
    allowedProjectIds: caps.allowed_project_ids || [],
    allowedEpicIds: caps.allowed_epic_ids || [],
  };
};

export default useCurrentUser;
